import { useDialog } from '@prismamedia/one-components';
import { format, isBefore } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { ArticleStatus } from '../../../__generated__/queries-web';
import { ThunkDispatch } from '../../../utils/thunkReducer';
import { actions, ArticleEditAction, ArticleState } from '../reducer';
import { ConfirmDialog } from './ConfirmDialog';

export const useStatusTransition = ({
  currentStatus,
  dispatch,
  newStatus,
  publishedAt,
  saveArticle,
  transitionType,
}: {
  currentStatus: ArticleStatus;
  dispatch: ThunkDispatch<ArticleEditAction>;
  newStatus: ArticleStatus | null;
  transitionType?: string | null;
  publishedAt: string | null | undefined;
  saveArticle: (forceArticle?: ArticleState) => void;
}) => {
  const { openDialog, closeDialog } = useDialog();

  const onCancel = useCallback(() => {
    dispatch((dispatchFn: any) => {
      dispatchFn(
        actions.changeStatus({
          status: currentStatus,
        }),
      );
    });
    closeDialog();
  }, [closeDialog, currentStatus, dispatch]);

  const updateArticle = useCallback(
    (status: ArticleStatus, publishedAtDate: Date) => {
      dispatch((dispatchFn: any, getState: any) => {
        dispatchFn(
          actions.updatePublishDateAndStatus({
            publishDate: publishedAtDate.toISOString(),
            status,
          }),
        );
        saveArticle(getState());
      });
      closeDialog();
    },
    [closeDialog, dispatch, saveArticle],
  );

  useEffect(() => {
    switch (newStatus) {
      case ArticleStatus.Published:
        // Draft to Published: always use current timestamp
        if (currentStatus === ArticleStatus.Draft) {
          updateArticle(ArticleStatus.Published, new Date());
          return;
        }

        if (!publishedAt) {
          updateArticle(ArticleStatus.Published, new Date());
          return;
        }

        if (currentStatus === ArticleStatus.Published) {
          if (transitionType === 'antidate') {
            openDialog(
              <ConfirmDialog
                title="Antidater l'article"
                message="Confirmez-vous d'antidater le"
                enableDateInput
                dateInit={publishedAt ? new Date(publishedAt) : new Date()}
                onValidate={(date) =>
                  updateArticle(ArticleStatus.Published, date)
                }
                onCancel={onCancel}
              />,
              { disableClose: true },
            );
            return;
          }

          openDialog(
            <ConfirmDialog
              title="Republier l'article"
              message="Confirmez-vous la republication ?"
              onValidate={() =>
                updateArticle(ArticleStatus.Published, new Date())
              }
              onCancel={onCancel}
            />,
            { disableClose: true },
          );
          return;
        }

        openDialog(
          <ConfirmDialog
            title="Confirmation de publication"
            message={`Cet article a une date de programmation définie pour le ${format(
              new Date(publishedAt),
              'PPPppp',
            )}.\n Souhaitez-vous tout de même le publier immédiatement ?`}
            onValidate={() =>
              updateArticle(ArticleStatus.Published, new Date(publishedAt))
            }
            onCancel={onCancel}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.Scheduled:
        openDialog(
          <ConfirmDialog
            title={
              publishedAt && isBefore(new Date(publishedAt), new Date())
                ? "Antidater l'article"
                : "Programmer l'article"
            }
            message="Confirmez-vous la programmation le"
            enableDateInput
            dateInit={publishedAt ? new Date(publishedAt) : new Date()}
            onValidate={(date) => updateArticle(ArticleStatus.Scheduled, date)}
            onCancel={onCancel}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.ScheduledForRepublication:
        openDialog(
          <ConfirmDialog
            title="Programmer la republication de l'article"
            message="Confirmez-vous la programmation le"
            enableDateInput
            dateInit={new Date()}
            onValidate={(date) =>
              updateArticle(ArticleStatus.ScheduledForRepublication, date)
            }
            onCancel={onCancel}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.Draft:
        if (
          currentStatus === ArticleStatus.Published &&
          (transitionType === 'unpublish' || transitionType === 'back2draft')
        ) {
          openDialog(
            <ConfirmDialog
              title="Dépublier un article"
              message="⚠️ Attention : Êtes-vous sûr(e) de vouloir dépublier cet article ?"
              onValidate={() => updateArticle(ArticleStatus.Draft, new Date())}
              onCancel={onCancel}
            />,
            { disableClose: true },
          );
          return;
        }

        dispatch((dispatchFn: any, getState: any) => {
          dispatchFn(
            actions.updatePublishDateAndStatus({
              publishDate: getState().publishedAt,
              status: newStatus,
            }),
          );
          saveArticle(getState());
        });
        break;

      case ArticleStatus.Frozen:
      case ArticleStatus.Ready:
      case ArticleStatus.Deleted:
      case ArticleStatus.Private:
        dispatch((dispatchFn: any, getState: any) => {
          dispatchFn(
            actions.updatePublishDateAndStatus({
              publishDate: getState().publishedAt,
              status: newStatus,
            }),
          );
          saveArticle(getState());
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus, newStatus, publishedAt, transitionType]);
};
